import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, Host, HostListener, OnDestroy, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as Chart from 'chart.js';
import { ConsumptionService } from '../../../services/consumption.service';
import { getParamFromAncestorRoute, getQueryParamFromAncestorRoute } from '../../../utilities/route.utilities';
import { AverageConsumptionGridItemComponent } from '../grid-item/average-consumption-grid-item.component';

@Component({
    selector: 'consumption-average-consumption-chart',
    template: '<canvas style="height:35rem;" id="basechart" #basechart></canvas>',
    styles: [':host { position: relative; display:block;}']
})
export class ConsumptionAverageConsumptionChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    supplyTypesData: any[] = [];
    filterList: any[] = [];
    filter: any[] = [];
    heatGradientStroke: any;
    hotWaterGradientStroke: any;
    coldWaterGradientStroke: any;
    supplyType: string;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number = 0;
    averageConsumption: any;
    initalLoad: boolean = false;
    hasCurrent: boolean = false;
    has_180365Days: boolean = false;
    has_365Days: boolean = false;
    has_6090Days: boolean = false;
    has_90180Days: boolean = false;
    hasBankTransfer: boolean = false;

    constructor(
        private consumptionService: ConsumptionService,
        private route: ActivatedRoute,
        private router: Router,
        @Host() private parent: AverageConsumptionGridItemComponent
    ) { }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        });
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId') ? getQueryParamFromAncestorRoute(this.route, 'siteId') : 0;
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');
        this.supplyType = getQueryParamFromAncestorRoute(this.route, 'supplyCategory') == null ? "Heat" : getQueryParamFromAncestorRoute(this.route, 'supplyCategory');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    unique = (value, index, self) => {
        return self.indexOf(value) === index;
    }

    firstLetterToLower(item: string) {
        return item.charAt(0).toLowerCase() + item.slice(1);
    }


    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {

        this.chartData = [];
        if (this.timePeriod == 12) {
            //monthly
            this.consumptionService.getAverageMeterMonthlyConsumption(this.clientId, this.supplyType, this.siteId).then((result) => {
                this.averageConsumption = result;
                this.chartData = this.processData(this.averageConsumption);
                this.parent.hasData = true;
                this.parent.hasLoading = false;
                this.createChart();
            }).catch(reason => {
                this.parent.hasData = false;
                this.parent.hasLoading = false;
            });
        }

        if (this.timePeriod == 90) {
            // daily
            this.consumptionService.getAverageMeterDailyConsumption(this.clientId, this.supplyType, this.siteId).then((result) => {
                this.averageConsumption = result;
                this.chartData = this.processData(this.averageConsumption);
                this.parent.hasData = true;
                this.parent.hasLoading = false;
                this.createChart();
            }).catch(reason => {
                this.parent.hasData = false;
                this.parent.hasLoading = false;
            });
        }
    }

    processData(data: any[]) {

        if (!data || data.length == 0) return;
        let monthCount: number = 0;
        this.hasCurrent = false;
        this.has_180365Days = false;
        this.has_365Days = false;
        this.has_6090Days = false;
        this.has_90180Days = false;

        let graphData = {
            labels: [],
            avgDailyUsage: [],
            highBoundary: [],
            lowBoundary: [],
            unit: ''
        };


        graphData.avgDailyUsage = data.map(item => {
            item.calendarYearMonth = item.calendarYearMonth.split('T')[0];
            return {
                x: item.calendarYearMonth,
                y: item.avgDailyUsage,
                label: item.unit
            }
        });

        graphData.highBoundary = data.filter(x => x.highBoundary !== null).map(item => {
            item.calendarYearMonth = item.calendarYearMonth.split('T')[0];
            return {
                x: item.calendarYearMonth,
                y: item.highBoundary,
                label: item.unit
            }
        });

        graphData.lowBoundary = data.filter(x => x.lowBoundary !== null).map(item => {
            item.calendarYearMonth = item.calendarYearMonth.split('T')[0];
            return {
                x: item.calendarYearMonth,
                y: item.lowBoundary,
                label: item.unit
            }
        });

        graphData.unit = data[0].unit;

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'line',
            options: {
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var hoverData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex] : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            return hoverData.label + ' (' + tooltipData.label + ') ' + parseFloat(tooltipData.y).toFixed(3);

                        }
                    }
                },
                scales: {
                    xAxes: [{
                        stacked: true,
                        barThickness: 20,
                        scaleLabel: {
                            display: true,
                            labelString: 'Month',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        type: 'time',
                        displayFormats: {
                            'month': 'MM',
                        },
                        gridLines: {
                            display: false
                        },
                        time: {
                            unit: 'month',
                            unitStepSize: 1,
                            displayFormats: {
                                'month': 'MMM'
                            }
                        },
                    }],
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true
                            },
                            id: "y-axis-0",
                            position: 'left',
                            scaleLabel: {
                                display: true,
                                labelString: 'Consumption ' + '(' + this.chartData.unit + ')',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            gridLines: {
                                display: true
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

        if (this.chartData.avgDailyUsage) {
            datasets.push({
                type: 'line',
                label: 'Average',
                yAxisID: 'y-axis-0',
                data: this.chartData.avgDailyUsage,
                backgroundColor: '#00539f',
                borderColor: '#00539f',
                pointBorderColor: '#00539f',
                pointBackgroundColor: '#00539f',
                pointHoverBackgroundColor: '#00539f',
                fill: false
            });
        }


        if ((this.chartData.highBoundary || this.chartData.lowBoundary) && this.chartData.highBoundary.length > 0) {
            datasets.push({
                type: 'line',
                label: 'Highest daily average',
                yAxisID: 'y-axis-0',
                data: this.chartData.highBoundary,
                backgroundColor: 'rgba(81, 81, 81, 0.2)',
                borderColor: '#9b9b9b',
                pointBorderColor: '#9b9b9b',
                pointBackgroundColor: '#9b9b9b',
                pointHoverBackgroundColor: '#9b9b9b',
                pointRadius: 0,
                pointHoverRadius: 0,
                borderWidth: 0.1,
                pointHoverBorderWidth: 0.1,
                lineThickness: 0.1,
                fill: false
            });
        }

        if ((this.chartData.lowBoundary || this.chartData.highBoundary) && this.chartData.lowBoundary.length > 0) {
            datasets.push({
                type: 'line',
                label: 'Lowest daily average',
                yAxisID: 'y-axis-0',
                data: this.chartData.lowBoundary,
                backgroundColor: 'rgba(81, 81, 81, 0.2)',
                borderColor: '#9b9b9b',
                pointBorderColor: '#9b9b9b',
                pointBackgroundColor: '#9b9b9b',
                pointHoverBackgroundColor: '#9b9b9b',
                pointRadius: 0,
                pointHoverRadius: 0,
                borderWidth: 0.1,
                pointHoverBorderWidth: 0.1,
                lineThickness: 0.1,
                fill: '-1'
            });
        }

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                datasets: datasets
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
